import { useEffect, useState } from "react";
import {
  DEFAULT_MESH_CONFIG,
  PREVIEW_EVENT,
  decodePreviewFromHash,
  fetchActiveMeshPreset,
  getPreviewMeshConfig,
  meshPreviewBus,
  migrateMeshConfig,
  type MeshConfig,
} from "@/lib/meshGradient";

/**
 * Returns the mesh gradient config to render on marketing pages.
 *
 * Precedence (highest wins):
 *   1. URL-hash preview (`#mesh=v2:...`) — cross-origin/cross-browser link from
 *      the admin "Open Landing" button. When present, NOTHING is allowed to
 *      overwrite it for the life of the page (no DB fetch, no localStorage
 *      "preview off" signal). The admin can still push live edits via
 *      BroadcastChannel updates.
 *   2. Same-origin localStorage preview (admin "Preview on marketing pages"
 *      switch in this same browser).
 *   3. BroadcastChannel live update from another tab.
 *   4. Active DB preset.
 *   5. DEFAULT_MESH_CONFIG.
 */
export const useMeshGradientConfig = (): MeshConfig => {
  const [config, setConfig] = useState<MeshConfig>(() => {
    const fromHash = decodePreviewFromHash();
    if (fromHash) return fromHash;
    return getPreviewMeshConfig() ?? migrateMeshConfig(DEFAULT_MESH_CONFIG);
  });

  useEffect(() => {
    let cancelled = false;
    // Hash preview is sticky for the page lifetime — never let it be overridden
    // by DB fetches or by an empty localStorage check.
    const hashPreview = decodePreviewFromHash();
    const hashPreviewActive = hashPreview !== null;

    // Tracks whether a *localStorage* preview is currently active. Hash preview
    // is tracked separately via hashPreviewActive (constant).
    let localPreviewActive = !hashPreviewActive && getPreviewMeshConfig() !== null;

    const loadDbFallback = () => {
      if (hashPreviewActive || localPreviewActive) return;
      void fetchActiveMeshPreset().then((cfg) => {
        if (cancelled || hashPreviewActive || localPreviewActive) return;
        setConfig(cfg ?? migrateMeshConfig(DEFAULT_MESH_CONFIG));
      });
    };

    const loadLocal = () => {
      // Hash preview is authoritative — ignore localStorage transitions.
      if (hashPreviewActive) return;
      const preview = getPreviewMeshConfig();
      if (preview) {
        localPreviewActive = true;
        setConfig(preview);
      } else if (localPreviewActive) {
        // Preview was turned off in this browser — fall back to DB.
        localPreviewActive = false;
        loadDbFallback();
      }
    };

    if (hashPreviewActive && hashPreview) {
      // Make sure state reflects the hash even on remount.
      setConfig(hashPreview);
    } else {
      loadLocal();
      loadDbFallback();
    }

    // Ask any sibling tabs (e.g. the admin tab with preview enabled) to
    // re-publish so we can pick up unsaved edits live.
    meshPreviewBus.request();

    const unsubBus = meshPreviewBus.subscribe((msg) => {
      if (msg.kind === "update") {
        if (msg.config) {
          // Live admin edits are allowed to update the rendered config even
          // when a hash preview is active (admin tab pushing newer state).
          setConfig(migrateMeshConfig(msg.config));
        } else if (!hashPreviewActive) {
          // Admin turned preview off — fall back to DB. Hash preview ignores.
          localPreviewActive = false;
          setConfig(migrateMeshConfig(DEFAULT_MESH_CONFIG));
          loadDbFallback();
        }
      }
    });

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        loadLocal();
        meshPreviewBus.request();
      }
    };
    const onFocus = () => {
      loadLocal();
      meshPreviewBus.request();
    };

    window.addEventListener(PREVIEW_EVENT, loadLocal);
    window.addEventListener("storage", loadLocal);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      unsubBus();
      window.removeEventListener(PREVIEW_EVENT, loadLocal);
      window.removeEventListener("storage", loadLocal);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return config;
};
